import { addDoc, collection, deleteDoc, doc, getDocs, orderBy, query, serverTimestamp, Timestamp, where, writeBatch } from 'firebase/firestore';

import { db } from '@/config/firebase';
import type {
  CreateFuneralAssistanceImportPayload,
  FuneralAssistanceCodeAssignment,
  FuneralAssistanceImportDocument,
  FuneralAssistanceImportTotals,
  FuneralAssistanceSourceFile,
  FuneralAssistanceAuditItem,
} from './funeralAssistanceTypes';

const FUNERAL_ASSISTANCE_IMPORTS_COLLECTION = 'expenses_funeral_assistance_imports';
const FUNERAL_ASSISTANCE_CODES_COLLECTION = 'expenses_funeral_assistance_codes';

function normalizeCode(code: string): string {
  return code?.toString().trim().replace(/\s+/g, '') ?? '';
}

export async function createFuneralAssistanceImport(
  provider: string,
  payload: CreateFuneralAssistanceImportPayload,
  user: { uid: string; email?: string | null; displayName?: string | null },
): Promise<FuneralAssistanceImportDocument> {
  const docRef = await addDoc(collection(db, FUNERAL_ASSISTANCE_IMPORTS_COLLECTION), {
    provider,
    billingPeriod: payload.billingPeriod ?? null,
    totals: payload.totals ?? { dependents: 0, feeCount: 0, amount: 0 },
    sourceFiles: payload.sourceFiles ?? [],
    recordsPreview: payload.recordsPreview ?? [],
    audit: payload.audit ?? [],
    createdAt: serverTimestamp(),
    createdByUid: user.uid,
    createdByEmail: user.email ?? null,
    createdByName: user.displayName ?? null,
  });

  return {
    id: docRef.id,
    provider,
    billingPeriod: payload.billingPeriod ?? null,
    totals: payload.totals ?? { dependents: 0, feeCount: 0, amount: 0 },
    sourceFiles: payload.sourceFiles ?? [],
    recordsPreview: payload.recordsPreview ?? [],
    audit: payload.audit ?? [],
    createdAt: new Date(),
    createdByUid: user.uid,
    createdByEmail: user.email ?? null,
    createdByName: user.displayName ?? null,
  };
}

export async function listFuneralAssistanceCodeAssignments(): Promise<Record<string, string>> {
  const snapshot = await getDocs(collection(db, FUNERAL_ASSISTANCE_CODES_COLLECTION));
  const assignments: Record<string, string> = {};
  snapshot.forEach((docSnapshot) => {
    const data = docSnapshot.data();
    const collaboratorId = typeof data?.collaboratorId === 'string' ? data.collaboratorId : '';
    if (!collaboratorId) return;
    assignments[docSnapshot.id] = collaboratorId;
  });
  return assignments;
}

export async function saveFuneralAssistanceCodeAssignments(
  assignments: FuneralAssistanceCodeAssignment[],
): Promise<void> {
  const normalized = assignments
    .map((assignment) => {
      const normalizedCode = normalizeCode(assignment.code);
      if (!normalizedCode) return null;
      return { code: normalizedCode, collaboratorId: assignment.collaboratorId };
    })
    .filter(Boolean) as FuneralAssistanceCodeAssignment[];

  if (!normalized.length) return;

  const batch = writeBatch(db);
  normalized.forEach(({ code, collaboratorId }) => {
    const ref = doc(db, FUNERAL_ASSISTANCE_CODES_COLLECTION, code);
    batch.set(ref, { collaboratorId, updatedAt: serverTimestamp() }, { merge: true });
  });

  await batch.commit();
}

export async function deleteFuneralAssistanceImportById(importId: string): Promise<void> {
  await deleteDoc(doc(db, FUNERAL_ASSISTANCE_IMPORTS_COLLECTION, importId));
}

function mapTotals(data: any): FuneralAssistanceImportTotals {
  if (!data || typeof data !== 'object') {
    return { dependents: 0, feeCount: 0, amount: 0 };
  }
  return {
    dependents: typeof data.dependents === 'number' ? data.dependents : 0,
    feeCount: typeof data.feeCount === 'number' ? data.feeCount : 0,
    amount: typeof data.amount === 'number' ? data.amount : 0,
  };
}

function mapSourceFiles(data: any): FuneralAssistanceSourceFile[] {
  if (!Array.isArray(data)) return [];
  return data
    .map((item) => ({
      name: typeof item?.name === 'string' ? item.name : 'unknown',
      size: typeof item?.size === 'number' ? item.size : 0,
      contentType: typeof item?.contentType === 'string' ? item.contentType : null,
    }))
    .filter((file) => file.name);
}

function mapDocument(docSnapshot: any): FuneralAssistanceImportDocument | null {
  const data = docSnapshot.data?.() ?? docSnapshot.data;
  if (!data) return null;
  const createdAt = data.createdAt instanceof Timestamp ? data.createdAt.toDate() : null;
  return {
    id: docSnapshot.id,
    provider: typeof data.provider === 'string' ? data.provider : '',
    billingPeriod: typeof data.billingPeriod === 'string' ? data.billingPeriod : null,
    totals: mapTotals(data.totals),
    sourceFiles: mapSourceFiles(data.sourceFiles),
    recordsPreview: Array.isArray(data.recordsPreview) ? (data.recordsPreview as any[]) : [],
    audit: Array.isArray(data.audit) ? (data.audit as FuneralAssistanceAuditItem[]) : [],
    createdAt,
    createdByUid: typeof data.createdByUid === 'string' ? data.createdByUid : '',
    createdByEmail: typeof data.createdByEmail === 'string' ? data.createdByEmail : null,
    createdByName: typeof data.createdByName === 'string' ? data.createdByName : null,
  };
}

export async function listFuneralAssistanceImports(): Promise<FuneralAssistanceImportDocument[]> {
  const importsQuery = query(collection(db, FUNERAL_ASSISTANCE_IMPORTS_COLLECTION), orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(importsQuery);
  return snapshot.docs.map(mapDocument).filter(Boolean) as FuneralAssistanceImportDocument[];
}

export async function listFuneralAssistanceImportsByCompetence(
  competence: string,
): Promise<FuneralAssistanceImportDocument[]> {
  const importsQuery = query(
    collection(db, FUNERAL_ASSISTANCE_IMPORTS_COLLECTION),
    where('billingPeriod', '==', competence),
  );
  const snapshot = await getDocs(importsQuery);
  return (snapshot.docs.map(mapDocument).filter(Boolean) as FuneralAssistanceImportDocument[]).sort(
    (a, b) => (b.createdAt?.getTime?.() ?? 0) - (a.createdAt?.getTime?.() ?? 0),
  );
}
